import { GET_ONE } from '../api';
import { isOk, serverErrorMessage } from '../utils';
import {
  DEFAULTS_CONFLICT_MESSAGE,
  defaultsInvalidMessage,
  isDefaultsConflict,
  isDefaultsInvalid,
} from './defaultTagCategoriesChanges';

/**
 * The answer to a Settings › General save (`PUT /tour_operator/{id}/`), for both pages that send one: the tabbed
 * page's hook (useGeneralSettings) and the legacy page (General.js, `features.tabbedGeneralSettings` off).
 *
 * The data provider resolves a refused save with its error body, so the answer is checked before anything uses
 * it: stored as the operator, the body broke every later save, and the page said "Settings saved" over it.
 *
 * Resolves true when the save was refused. Nothing was saved then: the caller keeps the form's edits and its
 * loaded snapshot (so Save stays enabled for them) and stops. Resolves false when the save succeeded.
 *
 * - 409 `defaults_conflict`: the default tag categories changed elsewhere since the page loaded them. The
 *   operator is fetched again and `onDefaultsReloaded(operator)` makes its list the new snapshot; the user
 *   re-applies their change to the fresh rows.
 * - 400 `defaults_invalid`: what is wrong, by category name (`rows`: the list the save was computed from).
 * - Any other refusal: the backend's message.
 */
export async function handleRefusedSettingsSave(res, { dataAPI, operatorId, rows, enqueueSnackbar, onDefaultsReloaded }) {
  if (isDefaultsConflict(res)) {
    try {
      const reloaded = await dataAPI(GET_ONE, 'tour_operator', { id: operatorId });
      if (!isOk(reloaded) || !Array.isArray(reloaded.data?.default_tag_categories)) {
        throw new Error('no default_tag_categories in the reloaded operator');
      }
      onDefaultsReloaded(reloaded.data);
      enqueueSnackbar(DEFAULTS_CONFLICT_MESSAGE, { variant: 'warning' });
    } catch (e) {
      enqueueSnackbar('Default tag categories were changed elsewhere — reload the page and re-apply your change', { variant: 'error' });
    }
    return true;
  }
  if (isDefaultsInvalid(res)) {
    enqueueSnackbar(defaultsInvalidMessage(res.data, rows), { variant: 'error' });
    return true;
  }
  if (!isOk(res)) {
    enqueueSnackbar(serverErrorMessage(res, 'Error saving settings'), { variant: 'error' });
    return true;
  }
  return false;
}
